import React, { useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import renderOptions from "../utils/textRenderOption";
import { getLargeImageUrl } from "../utils/imageLoader";
import "./Info.scss";

const Info = ({
  informations = [],
  activeInfoIds = [],
  setActiveInfoIds,
  setShowInfo,
}) => {
  // Find the active info or fallback to the first one with a background image
  const activeInfo =
    informations.find((info) => activeInfoIds.includes(info.sys?.id)) ||
    informations.find((info) => info.fields?.photoFond);

  const photoFond = activeInfo?.fields?.photoFond;
  const backgroundImage = photoFond ? getLargeImageUrl(photoFond) : "";

  const isContactActive = activeInfoIds.includes("6UiV9bwDFtnknAYi9Jz14k");

  const brightnessFactor = useMemo(() => {
    const count = activeInfoIds.length;
    if (count === 0) return 1;
    if (window.innerWidth <= 768) return 3;
    if (count === 1) return 1.5;
    if (count === 2) return 2.5;
    return 3.5;
  }, [activeInfoIds]);

  const handleBgLoad = useCallback((e) => {
    const img = e.target;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        img.style.opacity = "1";
      });
    });
  }, []);

  // Callback ref: handles cached images where onLoad may never fire
  const bgImgRef = useCallback((img) => {
    if (!img) return;
    if (img.complete && img.naturalWidth > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          img.style.opacity = "1";
        });
      });
    }
  }, []);

  const handleBackgroundClick = () => {
    if (activeInfoIds.length > 0 && setActiveInfoIds) {
      setActiveInfoIds([]);
    }
    if (setShowInfo && window.innerWidth > 768) {
      setShowInfo(false);
    }
  };

  return (
    <div
      className={`project-container ${window.innerWidth <= 768 && activeInfoIds.includes("4TlvwSJZOp8hFEg7UArlM2") ? "bio-active-mobile" : ""}`}
    >
      <Helmet>
        <title>Informations - Laure Philippe</title>
        <link rel="canonical" href="https://laurephilippe.fr/info" />
      </Helmet>

      {backgroundImage && (
        <div className={`info-background-wrapper ${brightnessFactor > 1 ? 'has-brightness' : ''}`}>
          <img
            src={backgroundImage}
            alt="Laure Phillipe info"
            onClick={handleBackgroundClick}
            width={photoFond?.fields?.file?.details?.image?.width}
            height={photoFond?.fields?.file?.details?.image?.height}
            className="info-background"
            ref={bgImgRef}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              opacity: 0,
              transition: "opacity 0.8s ease, filter 0.5s ease",
              filter: `brightness(${brightnessFactor})`,
              zIndex: 1,
            }}
            onLoad={handleBgLoad}
          />
        </div>
      )}

      <div
        className={`informations ${activeInfoIds.length > 0 ? "visible" : ""}`}
        onClick={handleBackgroundClick}
      >
        {informations
          .filter((info) => {
            const isCredits = info.sys?.id === "3OAm0VAjKQakYpRpoE3Cya";

            // If current info is Credits, only show if Contact is active
            if (isCredits) return isContactActive;

            // Otherwise show if it's in the active list
            return activeInfoIds.includes(info.sys?.id);
          })
          .map((info, idx) => (
            <div
              key={idx}
              className={`info-block info-id-${info.sys?.id}`}
              data-id={info.sys?.id}
              onClick={(e) => {
                e.stopPropagation();
                if (
                  window.innerWidth <= 768 &&
                  info.sys?.id === "4TlvwSJZOp8hFEg7UArlM2"
                ) {
                  if (setActiveInfoIds) {
                    setActiveInfoIds((prev) =>
                      prev.filter((id) => id !== "4TlvwSJZOp8hFEg7UArlM2"),
                    );
                  }
                }
              }}
            >
              {documentToReactComponents(info.fields?.texte, renderOptions)}
            </div>
          ))}
      </div>
    </div>
  );
};

export default Info;
